/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import {
  dismissWeekOneSetup,
  isWeekOneSetupDismissed,
  markWeekOneEssentialsSaved,
  skipWeekOneMpStep,
  skipWeekOnePrinterStep,
  skipWeekOneTeamStep,
} from '../application/onboardingUiStorage';
import { useHomeOnboardingCardsContext } from '../application/HomeOnboardingCardsContext';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useDismissableCard } from './useDismissableCard';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useTeamAccess } from '../../team/application/useTeamAccess';
import type { WeekOneSetupStepId } from '../application/useWeekOneSetup';

type Props = {
  userId: string;
  isOwner?: boolean;
  /** False en día 1 fresh (need_*). */
  offerAllowed?: boolean;
};

export function WeekOneSetupCard({ userId, isOwner = true, offerAllowed = true }: Props) {
  const navigation = useNavigation<any>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const canBranch = useHasBusinessCapability('employees');
  const { canInvite } = useTeamAccess();
  const { visible, onDismiss } = useDismissableCard(
    userId,
    isWeekOneSetupDismissed,
    dismissWeekOneSetup,
  );
  const onboarding = useHomeOnboardingCardsContext();
  const { loading, eligible, steps, allDone, mpStatus, reload } = onboarding?.weekOne ?? {
    loading: false,
    eligible: false,
    steps: [],
    allDone: true,
    mpStatus: null,
    reload: async () => undefined,
  };

  const goStep = useCallback(
    (stepId: WeekOneSetupStepId) => {
      switch (stepId) {
        case 'location':
          navigateInShell(navigation, 'BranchIdentity');
          return;
        case 'essentials':
          // El paso es "revisá las reglas" — entrar a Reglas del POS ya cumple.
          if (userId && branchId) {
            void markWeekOneEssentialsSaved(userId, branchId).then(() => void reload());
          }
          navigateInShell(navigation, 'BranchPosOps');
          return;
        case 'team':
          if (!canInvite) return;
          navigateInShell(navigation, 'InviteEmployee', {
            presetRoleId: 'cashier',
            branchIds: branchId ? [branchId] : undefined,
            weekOneFlow: true,
          });
          return;
        case 'printer':
          navigateInShell(navigation, 'BranchDevices');
          return;
        case 'mp':
          if (mpStatus?.oauth_connected && !mpStatus?.pos_configured) {
            navigateInShell(navigation, 'MercadoPagoBranchSettings');
          } else {
            navigateInShell(navigation, 'IntegrationsHub');
          }
          return;
        default:
          return;
      }
    },
    [branchId, canInvite, mpStatus, navigation, reload, userId],
  );

  const handleTeamSkip = useCallback(async () => {
    await skipWeekOneTeamStep(userId);
    await reload();
  }, [reload, userId]);

  const handleMpSkip = useCallback(async () => {
    await skipWeekOneMpStep(userId);
    await reload();
  }, [reload, userId]);

  const handlePrinterSkip = useCallback(async () => {
    await skipWeekOnePrinterStep(userId);
    await reload();
  }, [reload, userId]);

  /** Acción contextual de cada paso opcional — vive dentro de su fila, no suelta abajo. */
  const stepSkip: Partial<
    Record<WeekOneSetupStepId, { label: string; onPress: () => void; testID: string }>
  > = {
    ...(canInvite
      ? {
          team: {
            label: 'No tengo colaboradores',
            onPress: () => void handleTeamSkip(),
            testID: 'home.week-one-setup.team-skip',
          },
        }
      : {}),
    printer: {
      label: 'No tengo impresora',
      onPress: () => void handlePrinterSkip(),
      testID: 'home.week-one-setup.printer-skip',
    },
    mp: {
      label: 'Cobro solo efectivo',
      onPress: () => void handleMpSkip(),
      testID: 'home.week-one-setup.mp-skip',
    },
  };

  if (!isOwner || !offerAllowed || !visible || !canBranch || !eligible) return null;
  if (allDone) return null;

  return (
    <View style={styles.card} testID="home.week-one-setup">
      <View style={styles.header}>
        <Text style={styles.title}>Configurá tu local</Text>
        <TouchableOpacity
          onPress={() => void onDismiss()}
          accessibilityRole="button"
          accessibilityLabel="Ocultar por ahora"
          testID="home.week-one-setup.dismiss"
        >
          <Ionicons name="close" size={18} color={ticketColors.ink2} />
        </TouchableOpacity>
      </View>
      <Text style={styles.subtitle}>Para la primera semana. Podés seguir vendiendo.</Text>

      {loading && steps.length === 0 ? (
        <ActivityIndicator color={ticketColors.gold} />
      ) : (
        steps.map((step) => {
          const skip = !step.done && !step.skipped ? stepSkip[step.id] : undefined;
          return (
            <View key={step.id} style={styles.rowBlock}>
              <TouchableOpacity
                style={styles.row}
                onPress={() => goStep(step.id)}
                disabled={step.id === 'team' && !canInvite}
                accessibilityRole="button"
                accessibilityLabel={step.label}
                testID={`home.week-one-setup.step.${step.id}`}
              >
                <Ionicons
                  name={step.done ? 'checkmark-circle' : 'ellipse-outline'}
                  size={20}
                  color={step.done ? ticketColors.ok : ticketColors.ink2}
                />
                <View style={styles.rowText}>
                  <View style={styles.rowTitleLine}>
                    <Text style={styles.rowTitle}>{step.label}</Text>
                    {step.optional && !step.done && !step.skipped ? (
                      <Text style={styles.optionalBadge}>Opcional</Text>
                    ) : null}
                  </View>
                  <Text style={styles.rowHint}>{step.hint}</Text>
                </View>
                {!step.done || step.skipped ? (
                  <Ionicons name="chevron-forward" size={18} color={ticketColors.ink2} />
                ) : null}
              </TouchableOpacity>
              {skip ? (
                <TouchableOpacity
                  style={styles.stepSkipBtn}
                  onPress={skip.onPress}
                  accessibilityRole="button"
                  testID={skip.testID}
                >
                  <Text style={styles.stepSkipText}>{skip.label}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          );
        })
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...ticketSurfaces.paperSheet,
    gap: spacing.sm,
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
    paddingRight: spacing.sm,
  },
  subtitle: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
    marginTop: -4,
  },
  rowBlock: {
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    gap: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rowText: { flex: 1, minWidth: 0, gap: 2 },
  rowTitleLine: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  optionalBadge: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  rowHint: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  stepSkipBtn: {
    alignSelf: 'flex-start',
    // Alineado con el texto del paso (icono 20 + gap sm).
    marginLeft: 20 + spacing.sm,
    paddingVertical: 4,
  },
  stepSkipText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
